import "./OperatorComparePanel.css";

/**
 * Phase 18 WU-18.E3 — compare tray.
 *
 * Docked at the bottom of the Operator Performance tab when ≥2 operators are
 * selected (max 4; the parent enforces the upper bound). Renders a small
 * table of side-by-side metrics.
 */
export interface OperatorCompareRow {
  operatorId: string;
  operatorName: string;
  docs: number;
  avgKpm: number;
  avgCorrectionRate: number;
  avgTimePerFieldMs: number;
}

export interface OperatorComparePanelProps {
  rows: OperatorCompareRow[];
  onDismiss: () => void;
}

function formatPct(r: number): string {
  return `${(r * 100).toFixed(1)}%`;
}

function formatMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function OperatorComparePanel({ rows, onDismiss }: OperatorComparePanelProps) {
  if (rows.length < 2) return null;

  return (
    <div className="operator-compare" data-testid="operator-compare">
      <div className="operator-compare__header">
        <span className="operator-compare__title">
          Comparing {rows.length} Operators
        </span>
        <button
          type="button"
          className="operator-compare__close"
          aria-label="Close compare tray"
          onClick={onDismiss}
        >
          ×
        </button>
      </div>
      <table className="operator-compare__table">
        <thead>
          <tr>
            <th>Operator</th>
            <th>Docs</th>
            <th>Avg KPM</th>
            <th>Correction Rate</th>
            <th>Avg Time / Field</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.operatorId}
              data-testid={`operator-compare-row-${r.operatorId}`}
            >
              <td>{r.operatorName}</td>
              <td>{r.docs.toLocaleString()}</td>
              <td>{r.avgKpm.toFixed(1)}</td>
              <td>{formatPct(r.avgCorrectionRate)}</td>
              <td>{formatMs(r.avgTimePerFieldMs)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
